import type { BaseSortState } from 'noddi-types';

/**
 * The slice of a column definition that server-side sorting reads. Structural
 * so `useNoddiSortOrderParams` (noddi-web) can take a table's columns without
 * caring about `TData`.
 */
export type SortableColumn<TOrderingKey extends string = string> = {
  id?: string;
  accessorKey?: string;
  meta?: { orderingKey?: TOrderingKey };
};

/** A sort key as it appears in the `ordering` query parameter: `-`-prefixed when descending. */
export type OrderingParam<TOrderingKey extends string> = TOrderingKey | `-${TOrderingKey}`;

/** The key a column sorts by, i.e. what it sends in `ordering`. `undefined` when it is not sortable. */
export const getColumnSortKey = <TOrderingKey extends string>(
  column: SortableColumn<TOrderingKey>
): TOrderingKey | undefined => column.meta?.orderingKey;

const fromOrderingParam = <TOrderingKey extends string>(param: string): BaseSortState<TOrderingKey> =>
  param.startsWith('-')
    ? { column: param.slice(1) as TOrderingKey, order: 'desc' }
    : { column: param as TOrderingKey, order: 'asc' };

/**
 * The canonical sort key of the table column a URL value refers to. Accepts
 * the key itself and, for links that predate `orderingKey`, the accessorKey /
 * id the URL used to carry. `null` when no sortable column matches.
 */
const resolveSortColumn = <TOrderingKey extends string>(
  raw: string,
  columns: readonly SortableColumn<TOrderingKey>[]
): TOrderingKey | null => {
  const keyed = columns.flatMap((column) => {
    const key = getColumnSortKey(column);
    return key ? [{ key, aliases: [column.accessorKey, column.id] }] : [];
  });
  const exact = keyed.find((column) => column.key === raw);
  if (exact) {
    return exact.key;
  }
  return keyed.find((column) => column.aliases.includes(raw))?.key ?? null;
};

/**
 * The sort state `ordering` query-param values describe.
 *
 * Sorting is one column at a time, so only the first usable value is kept: a
 * multi-key URL (stale link, hand-edited) would otherwise sort on its first
 * key while every listed column's header showed an arrow.
 *
 * With `columns`, values the table cannot sort on are skipped and legacy
 * accessor keys are rewritten to the canonical key, so such a URL can neither
 * 400 the list nor leave a phantom sort the header cannot show or clear.
 * Without `columns` (untyped tables) the value passes through unchecked.
 */
export const parseOrderingParams = <TOrderingKey extends string>(
  params: readonly (string | null)[] | null | undefined,
  columns?: readonly SortableColumn<TOrderingKey>[]
): BaseSortState<TOrderingKey> | null => {
  for (const param of params ?? []) {
    if (!param) {
      continue;
    }
    const { column, order } = fromOrderingParam<TOrderingKey>(param);
    if (!columns) {
      return { column, order };
    }
    const resolved = resolveSortColumn(column, columns);
    if (resolved !== null) {
      return { column: resolved, order };
    }
  }
  return null;
};

/** `ordering` query-param values for a sort state; a null order clears the sort. */
export const serializeOrdering = (sort: BaseSortState<string> | null): string[] => {
  if (!sort?.order) {
    return [];
  }
  return [sort.order === 'desc' ? `-${sort.column}` : sort.column];
};
